/**
 * Light/dark theme control. The site follows the OS setting by default (see the
 * `prefers-color-scheme` rules in tokens.css); once the visitor picks a theme we
 * persist an explicit choice in localStorage and reflect it on <html data-theme>,
 * which overrides the system preference. `THEME_SNIPPET` runs before paint (in
 * the root layout) so the chosen theme is applied with no flash of the wrong one.
 */

export type Theme = 'light' | 'dark';

export const THEME_KEY = 'vn-theme';

/** Inline, dependency-free script that applies the saved theme before first paint. */
export const THEME_SNIPPET = `(function(){try{var t=localStorage.getItem('${THEME_KEY}');if(t==='dark'||t==='light'){document.documentElement.setAttribute('data-theme',t);}}catch(e){}})();`;

export function getStoredTheme(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

export function systemTheme(): Theme {
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/** The theme currently in effect: an explicit choice, else the system setting. */
export function effectiveTheme(): Theme {
  return getStoredTheme() ?? systemTheme();
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* storage unavailable — still apply for this session */
  }
  document.documentElement.setAttribute('data-theme', theme);
}
